import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useState } from 'react'

export type RestartStatus = 'restarting' | 'success' | 'error' | null

interface RestartStatusState {
  [controllerId: string]: RestartStatus
}

interface ControllerRestartContextType {
  restartStatuses: RestartStatusState
  setRestartInProgress: (controllerId: string) => void
  setRestartSuccess: (controllerId: string) => void
  setRestartError: (controllerId: string) => void
  clearRestartStatus: (controllerId: string) => void
}

interface ControllerRestartProviderProps {
  children: ReactNode
}

const ControllerRestartContext = createContext<ControllerRestartContextType | undefined>(undefined)

export function ControllerRestartProvider({ children }: Readonly<ControllerRestartProviderProps>) {
  const [restartStatuses, setRestartStatuses] = useState<RestartStatusState>({})

  const updateStatus = useCallback((controllerId: string, status: RestartStatus) => {
    setRestartStatuses((prev) => ({
      ...prev,
      [controllerId]: status,
    }))
  }, [])

  const setRestartInProgress = useCallback(
    (controllerId: string) => {
      updateStatus(controllerId, 'restarting')
    },
    [updateStatus],
  )

  const setRestartSuccess = useCallback(
    (controllerId: string) => {
      updateStatus(controllerId, 'success')
    },
    [updateStatus],
  )

  const setRestartError = useCallback(
    (controllerId: string) => {
      updateStatus(controllerId, 'error')
    },
    [updateStatus],
  )

  const clearRestartStatus = useCallback((controllerId: string) => {
    setRestartStatuses((prev) => {
      const next = { ...prev }
      delete next[controllerId]
      return next
    })
  }, [])

  const value: ControllerRestartContextType = {
    restartStatuses,
    setRestartInProgress,
    setRestartSuccess,
    setRestartError,
    clearRestartStatus,
  }

  return <ControllerRestartContext.Provider value={value}>{children}</ControllerRestartContext.Provider>
}

const useControllerRestartContext = () => {
  const context = useContext(ControllerRestartContext)
  if (!context) {
    throw new Error('useControllerRestartContext must be used within ControllerRestartProvider')
  }
  return context
}

export { useControllerRestartContext }
